import { supabase } from "../Services/supabaseClient";

/* =========================================================
   BOOTSTRAP
========================================================= */

export async function ensurePayrollBootstrap(companyId) {
  if (!companyId) throw new Error("companyId is required");

  const { error: settingsError } = await supabase.rpc(
    "ensure_payroll_settings",
    {
      p_company_id: companyId,
    }
  );
  if (settingsError) throw settingsError;

  const { error: seedError } = await supabase.rpc(
    "seed_default_payroll_components",
    {
      p_company_id: companyId,
    }
  );
  if (seedError) throw seedError;
}

export async function ensurePayrollPhase2Bootstrap(companyId) {
  if (!companyId) throw new Error("companyId is required");

  const { error } = await supabase.rpc("ensure_payroll_phase2_bootstrap", {
    p_company_id: companyId,
  });

  if (error) throw error;
}

export async function ensurePayrollPhase3Bootstrap(companyId) {
  if (!companyId) throw new Error("companyId is required");

  const { error } = await supabase.rpc("ensure_payroll_phase3_bootstrap", {
    p_company_id: companyId,
  });

  if (error) throw error;
}

export async function previewPayrollRun(companyId, payrollRunId) {
  const { data, error } = await supabase.functions.invoke(
    "preview-payroll-run",
    {
      body: {
        company_id: companyId,
        payroll_run_id: payrollRunId,
      },
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function getPayrollSettings(companyId) {
  const { data, error } = await supabase
    .from("payroll_settings")
    .select("*")
    .eq("company_id", companyId)
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayrollSettings(companyId, payload) {
  const { data, error } = await supabase
    .from("payroll_settings")
    .update(payload)
    .eq("company_id", companyId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   DASHBOARD
========================================================= */

export async function getPayrollDashboard(companyId) {
  const [
    employeesRes,
    runsRes,
    periodsRes,
    componentsRes,
    anomaliesRes,
    paymentsRes,
  ] = await Promise.all([
    supabase
      .from("payroll_employees")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .eq("employment_status", "active"),

    supabase
      .from("payroll_runs")
      .select("id, run_name, net_total, gross_total, status, created_at")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false })
      .limit(5),

    supabase
      .from("payroll_periods")
      .select("id, period_name, status, payment_date")
      .eq("company_id", companyId)
      .order("payment_date", { ascending: false })
      .limit(5),

    supabase
      .from("payroll_pay_components")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId),

    supabase
      .from("payroll_anomalies")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId)
      .eq("resolved", false),

    supabase
      .from("payroll_payment_batches")
      .select("id", { count: "exact", head: true })
      .eq("company_id", companyId),
  ]);

  if (employeesRes.error) throw employeesRes.error;
  if (runsRes.error) throw runsRes.error;
  if (periodsRes.error) throw periodsRes.error;
  if (componentsRes.error) throw componentsRes.error;
  if (anomaliesRes.error) throw anomaliesRes.error;
  if (paymentsRes.error) throw paymentsRes.error;

  return {
    activeEmployees: employeesRes.count || 0,
    recentRuns: runsRes.data || [],
    recentPeriods: periodsRes.data || [],
    componentCount: componentsRes.count || 0,
    openAnomalies: anomaliesRes.count || 0,
    paymentBatchCount: paymentsRes.count || 0,
  };
}

/* =========================================================
   EMPLOYEES
========================================================= */

export async function getEmployees(companyId) {
  const { data, error } = await supabase
    .from("payroll_employees")
    .select(
      `
      *,
      payroll_job_levels (
        id,
        level_code,
        level_name
      ),
      payroll_job_grades (
        id,
        grade_code,
        grade_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;

  return (data || []).map((row) => ({
    ...row,
    job_level_code: pickJoined(row.payroll_job_levels)?.level_code || "",
    job_level_name: pickJoined(row.payroll_job_levels)?.level_name || "",
    job_grade_code: pickJoined(row.payroll_job_grades)?.grade_code || "",
    job_grade_name: pickJoined(row.payroll_job_grades)?.grade_name || "",
  }));
}

function pickJoined(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export async function getEmployee(employeeId) {
  const { data, error } = await supabase
    .from("payroll_employees")
    .select("*")
    .eq("id", employeeId)
    .single();

  if (error) throw error;
  return data;
}

export async function createEmployee(payload) {
  const { data, error } = await supabase
    .from("payroll_employees")
    .insert(payload)
    .select()
    .single();

  if (error) {
    console.error("createEmployee error:", error);
    throw new Error(error.message || "Failed to create employee");
  }

  return data;
}

export async function updateEmployee(id, payload) {
  const { data, error } = await supabase
    .from("payroll_employees")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteEmployee(id) {
  const { error } = await supabase
    .from("payroll_employees")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

/* =========================================================
   PAY COMPONENTS
========================================================= */

export async function getPayComponents(companyId) {
  const { data, error } = await supabase
    .from("payroll_pay_components")
    .select("*")
    .eq("company_id", companyId)
    .order("component_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createPayComponent(payload) {
  const { data, error } = await supabase
    .from("payroll_pay_components")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayComponent(id, payload) {
  const { data, error } = await supabase
    .from("payroll_pay_components")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function assignComponentToEmployee(payload) {
  const { data, error } = await supabase
    .from("payroll_employee_components")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateEmployeeComponent(id, payload) {
  const { data, error } = await supabase
    .from("payroll_employee_components")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteEmployeeComponent(id) {
  const { error } = await supabase
    .from("payroll_employee_components")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function getEmployeeComponents(companyId, employeeId) {
  const { data, error } = await supabase
    .from("payroll_employee_components")
    .select(
      `
      *,
      payroll_pay_components (
        id,
        component_code,
        component_name,
        component_type,
        amount_type,
        taxable,
        pensionable
      )
    `
    )
    .eq("company_id", companyId)
    .eq("employee_id", employeeId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

/* =========================================================
   CALENDARS / PERIODS
========================================================= */

export async function getCalendars(companyId) {
  const { data, error } = await supabase
    .from("payroll_calendars")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createCalendar(payload) {
  const { data, error } = await supabase
    .from("payroll_calendars")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCalendar(id, payload) {
  const { data, error } = await supabase
    .from("payroll_calendars")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPeriods(companyId) {
  const { data, error } = await supabase
    .from("payroll_periods")
    .select(
      `
      *,
      payroll_calendars (
        id,
        calendar_name,
        pay_frequency
      )
    `
    )
    .eq("company_id", companyId)
    .order("payment_date", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createPeriod(payload) {
  const { data, error } = await supabase
    .from("payroll_periods")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePeriod(id, payload) {
  const { data, error } = await supabase
    .from("payroll_periods")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   PAYROLL RUNS
========================================================= */

export async function getPayrollRuns(companyId) {
  const { data, error } = await supabase
    .from("payroll_runs")
    .select(
      `
      *,
      payroll_periods (
        id,
        period_name,
        period_start,
        period_end,
        payment_date
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createPayrollRun(payload) {
  const { data, error } = await supabase
    .from("payroll_runs")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayrollRun(id, payload) {
  const { data, error } = await supabase
    .from("payroll_runs")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollRunDetails(runId) {
  const { data: run, error: runError } = await supabase
    .from("payroll_runs")
    .select(
      `
      *,
      payroll_periods (
        id,
        period_name,
        period_start,
        period_end,
        payment_date
      )
    `
    )
    .eq("id", runId)
    .single();

  if (runError) throw runError;

  const { data: employees, error: empError } = await supabase
    .from("payroll_run_employees")
    .select("*")
    .eq("payroll_run_id", runId)
    .order("employee_name", { ascending: true });

  if (empError) throw empError;

  return {
    run,
    employees: employees || [],
  };
}

export async function getPayrollRunLines(runId, employeeId) {
  const { data, error } = await supabase
    .from("payroll_result_lines")
    .select("*")
    .eq("payroll_run_id", runId)
    .eq("employee_id", employeeId)
    .order("sequence_no", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function processPayrollRun(companyId, payrollRunId) {
  const { data, error } = await supabase.functions.invoke(
    "process-payroll-run-v2",
    {
      body: {
        company_id: companyId,
        payroll_run_id: payrollRunId,
      },
    }
  );

  console.log("processPayrollRun data:", data);
  console.log("processPayrollRun error:", error);

  if (error) {
    throw new Error(
      data?.error || error.message || "Failed to process payroll"
    );
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data;
}

export async function processPayrollRunV2(companyId, payrollRunId) {
  const { data, error } = await supabase.functions.invoke(
    "process-payroll-run-v2",
    {
      body: {
        company_id: companyId,
        payroll_run_id: payrollRunId,
      },
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/* =========================================================
   PAYSLIPS
========================================================= */

export async function getPayslips(companyId) {
  const { data, error } = await supabase
    .from("payroll_payslips")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      ),
      payroll_runs (
        id,
        run_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

/* =========================================================
   ATTENDANCE / TIMESHEETS
========================================================= */

export async function getAttendance(companyId) {
  const { data, error } = await supabase
    .from("payroll_attendance_logs")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("attendance_date", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function saveAttendance(payload) {
  const { data, error } = await supabase
    .from("payroll_attendance_logs")
    .upsert(payload, { onConflict: "employee_id,attendance_date" })
    .select();

  if (error) throw error;
  return data;
}

export async function getTimesheets(companyId) {
  const { data, error } = await supabase
    .from("payroll_timesheets")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("period_start", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createTimesheet(payload) {
  const { data, error } = await supabase
    .from("payroll_timesheets")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateTimesheet(id, payload) {
  const { data, error } = await supabase
    .from("payroll_timesheets")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   LEAVE
========================================================= */

export async function getLeaveTypes(companyId) {
  const { data, error } = await supabase
    .from("payroll_leave_types")
    .select("*")
    .eq("company_id", companyId)
    .order("leave_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createLeaveType(payload) {
  const { data, error } = await supabase
    .from("payroll_leave_types")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateLeaveType(id, payload) {
  const { data, error } = await supabase
    .from("payroll_leave_types")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getLeaveBalances(companyId) {
  const { data, error } = await supabase
    .from("payroll_leave_balances")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      ),
      payroll_leave_types (
        id,
        leave_name,
        leave_code
      )
    `
    )
    .eq("company_id", companyId)
    .order("year", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createLeaveBalance(payload) {
  const { data, error } = await supabase
    .from("payroll_leave_balances")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateLeaveBalance(id, payload) {
  const { data, error } = await supabase
    .from("payroll_leave_balances")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getLeaveRequests(companyId) {
  const { data, error } = await supabase
    .from("payroll_leave_requests")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      ),
      payroll_leave_types (
        id,
        leave_name,
        leave_code
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createLeaveRequest(payload) {
  const { data, error } = await supabase
    .from("payroll_leave_requests")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateLeaveRequest(id, payload) {
  const { data, error } = await supabase
    .from("payroll_leave_requests")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   BENEFITS / LOANS
========================================================= */

export async function getBenefitPlans(companyId) {
  const { data, error } = await supabase
    .from("payroll_benefit_plans")
    .select("*")
    .eq("company_id", companyId)
    .order("benefit_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createBenefitPlan(payload) {
  const { data, error } = await supabase
    .from("payroll_benefit_plans")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateBenefitPlan(id, payload) {
  const { data, error } = await supabase
    .from("payroll_benefit_plans")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getEmployeeBenefits(companyId) {
  const { data, error } = await supabase
    .from("payroll_employee_benefits")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      ),
      payroll_benefit_plans (
        id,
        benefit_name,
        benefit_code,
        benefit_type
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function assignBenefitToEmployee(payload) {
  const { data, error } = await supabase
    .from("payroll_employee_benefits")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateEmployeeBenefit(id, payload) {
  const { data, error } = await supabase
    .from("payroll_employee_benefits")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getLoans(companyId) {
  const { data, error } = await supabase
    .from("payroll_loans")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createLoan(payload) {
  const { data, error } = await supabase
    .from("payroll_loans")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateLoan(id, payload) {
  const { data, error } = await supabase
    .from("payroll_loans")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   COMPLIANCE / STATUTORY
========================================================= */

export async function getStatutoryRules(companyId) {
  const { data, error } = await supabase
    .from("payroll_statutory_rules")
    .select("*")
    .eq("company_id", companyId)
    .order("rule_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getStatutoryRuleById(id) {
  const { data, error } = await supabase
    .from("payroll_statutory_rules")
    .select("*")
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

export async function createStatutoryRule(payload) {
  const { data, error } = await supabase
    .from("payroll_statutory_rules")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateStatutoryRule(id, payload) {
  const { data, error } = await supabase
    .from("payroll_statutory_rules")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getEmployeeStatutoryProfiles(companyId) {
  const { data, error } = await supabase
    .from("payroll_employee_statutory_profiles")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function upsertEmployeeStatutoryProfile(payload) {
  const { data, error } = await supabase
    .from("payroll_employee_statutory_profiles")
    .upsert(payload, { onConflict: "employee_id" })
    .select();

  if (error) throw error;
  return data;
}

/* =========================================================
   APPROVALS
========================================================= */

export async function getApprovalSteps(companyId) {
  const { data, error } = await supabase
    .from("payroll_approval_steps")
    .select("*")
    .eq("company_id", companyId)
    .order("step_order", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createApprovalStep(payload) {
  const { data, error } = await supabase
    .from("payroll_approval_steps")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateApprovalStep(id, payload) {
  const { data, error } = await supabase
    .from("payroll_approval_steps")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getApprovals(companyId) {
  const { data, error } = await supabase
    .from("payroll_run_approvals")
    .select(
      `
      *,
      payroll_runs (
        id,
        run_name,
        status
      ),
      payroll_approval_steps (
        id,
        step_name,
        step_order
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function approvePayrollRun(
  companyId,
  payrollRunId,
  actionBy,
  notes = ""
) {
  const { data, error } = await supabase.functions.invoke(
    "approve-payroll-run",
    {
      body: {
        company_id: companyId,
        payroll_run_id: payrollRunId,
        action_by: actionBy,
        notes,
      },
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/* =========================================================
   PAYMENTS
========================================================= */

export async function generatePaymentBatch(payload) {
  const { data, error } = await supabase.functions.invoke(
    "generate-payment-batch",
    {
      body: payload,
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function getPaymentBatches(companyId) {
  const { data, error } = await supabase
    .from("payroll_payment_batches")
    .select(
      `
      *,
      payroll_runs (
        id,
        run_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getPaymentBatchLines(batchId) {
  const { data, error } = await supabase
    .from("payroll_payment_batch_lines")
    .select("*")
    .eq("payment_batch_id", batchId)
    .order("employee_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function updatePaymentBatch(id, payload) {
  const { data, error } = await supabase
    .from("payroll_payment_batches")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   ACCOUNT MAPPINGS / JOURNALS
========================================================= */
export async function ensurePayrollJournalBootstrap(companyId) {
  if (!companyId) throw new Error("companyId is required");

  const { error } = await supabase.rpc("ensure_payroll_journal_bootstrap", {
    p_company_id: companyId,
  });

  if (error) throw error;
}

export async function getPayrollAccountMappings(companyId) {
  const { data, error } = await supabase
    .from("payroll_account_mappings")
    .select("*")
    .eq("company_id", companyId)
    .order("mapping_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getPayrollJournalMappings(companyId) {
  const { data, error } = await supabase
    .from("payroll_account_mappings")
    .select("*")
    .eq("company_id", companyId)
    .order("mapping_name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function validatePayrollJournalMappings(companyId) {
  const { data, error } = await supabase.rpc(
    "validate_payroll_journal_mappings",
    {
      p_company_id: companyId,
    }
  );

  if (error) throw error;
  return data || [];
}

export async function upsertPayrollAccountMapping(payload) {
  const { data, error } = await supabase
    .from("payroll_account_mappings")
    .upsert(payload, { onConflict: "company_id,mapping_code" })
    .select();

  if (error) throw error;
  return data;
}

export async function upsertPayrollJournalMapping(payload) {
  const { data, error } = await supabase
    .from("payroll_account_mappings")
    .upsert(payload, { onConflict: "company_id,mapping_code" })
    .select();

  if (error) throw error;
  return data;
}

export async function getCompanyCoaAccounts(companyId) {
  const { data, error } = await supabase
    .from("chart_of_accounts")
    .select("id, account_number, name, category, type")
    .eq("company_id", companyId)
    .order("name", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getPayrollJournalBatches(companyId) {
  const { data, error } = await supabase
    .from("payroll_journal_batches")
    .select(
      `
      *,
      payroll_runs (
        id,
        run_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function getPayrollJournalLines(batchId) {
  const { data, error } = await supabase
    .from("payroll_journal_lines")
    .select("*")
    .eq("journal_batch_id", batchId)
    .order("line_no", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function postPayrollToJournal({
  companyId,
  payrollRunId,
  createdBy = null,
  postingDate = null,
  notes = "",
}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

  const res = await fetch(
    `${supabaseUrl}/functions/v1/post-payroll-to-journal`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anonKey,
        Authorization: `Bearer ${session?.access_token || ""}`,
      },
      body: JSON.stringify({
        company_id: companyId,
        payroll_run_id: payrollRunId,
        created_by: createdBy,
        posting_date: postingDate,
        notes,
      }),
    }
  );

  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  console.log("post-payroll-to-journal status:", res.status);
  console.log("post-payroll-to-journal body:", body);

  if (!res.ok) {
    throw new Error(
      body?.error || `HTTP ${res.status} posting payroll to journal`
    );
  }

  return body;
}
/* =========================================================
   PORTAL / DOCUMENTS / QUERIES
========================================================= */

export async function getPortalAccess(companyId) {
  const { data, error } = await supabase
    .from("payroll_employee_portal_access")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createPortalAccess(payload) {
  const { data, error } = await supabase
    .from("payroll_employee_portal_access")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePortalAccess(id, payload) {
  const { data, error } = await supabase
    .from("payroll_employee_portal_access")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollDocuments(companyId) {
  const { data, error } = await supabase
    .from("payroll_documents")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createPayrollDocument(payload) {
  const { data, error } = await supabase
    .from("payroll_documents")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollQueries(companyId) {
  const { data, error } = await supabase
    .from("payroll_queries")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function createPayrollQuery(payload) {
  const { data, error } = await supabase
    .from("payroll_queries")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayrollQuery(id, payload) {
  const { data, error } = await supabase
    .from("payroll_queries")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   ANOMALIES
========================================================= */

export async function getAnomalies(companyId) {
  const { data, error } = await supabase
    .from("payroll_anomalies")
    .select(
      `
      *,
      payroll_employees (
        id,
        employee_code,
        first_name,
        last_name
      ),
      payroll_runs (
        id,
        run_name
      )
    `
    )
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function updateAnomaly(id, payload) {
  const { data, error } = await supabase
    .from("payroll_anomalies")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function resolveAnomaly(id, payload = {}) {
  const { data, error } = await supabase
    .from("payroll_anomalies")
    .update({
      resolved: true,
      resolved_at: payload.resolved_at || new Date().toISOString(),
      ...payload,
    })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/* =========================================================
   SCENARIOS
========================================================= */

export async function getScenarios(companyId) {
  const { data, error } = await supabase
    .from("payroll_scenarios")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function runScenario(payload) {
  const { data, error } = await supabase.functions.invoke(
    "run-payroll-scenario",
    {
      body: payload,
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/* =========================================================
   PAYSLIP
========================================================= */

export async function getPayslipData(companyId, payrollRunId, employeeId) {
  const runEmployeeRes = await supabase
    .from("payroll_run_employees")
    .select("*")
    .eq("company_id", companyId)
    .eq("payroll_run_id", payrollRunId)
    .eq("employee_id", employeeId)
    .single();

  if (runEmployeeRes.error) throw runEmployeeRes.error;

  const runEmployee = runEmployeeRes.data;

  const [breakdownRes, employeeMasterRes] = await Promise.all([
    supabase
      .from("payroll_result_lines")
      .select("*")
      .eq("company_id", companyId)
      .eq("payroll_run_id", payrollRunId)
      .eq("employee_id", employeeId)
      .order("sequence_no", { ascending: true }),

    supabase
      .from("payroll_employees")
      .select(
        `
        *,
        payroll_job_levels (
          id,
          level_code,
          level_name
        ),
        payroll_job_grades (
          id,
          grade_code,
          grade_name
        )
      `
      )
      .eq("company_id", companyId)
      .eq("id", employeeId)
      .single(),
  ]);

  if (breakdownRes.error) throw breakdownRes.error;
  if (employeeMasterRes.error) throw employeeMasterRes.error;

  const employeeMaster = employeeMasterRes.data || {};
  const jobLevel = pickJoined(employeeMaster.payroll_job_levels);
  const jobGrade = pickJoined(employeeMaster.payroll_job_grades);

  const employeeSummary = {
    ...runEmployee,
    first_name: employeeMaster.first_name || "",
    last_name: employeeMaster.last_name || "",
    employee_name:
      runEmployee.employee_name ||
      `${employeeMaster.first_name || ""} ${
        employeeMaster.last_name || ""
      }`.trim(),
    employee_code:
      runEmployee.employee_code || employeeMaster.employee_code || "",
    department: runEmployee.department || employeeMaster.department || "",
    job_title: runEmployee.job_title || employeeMaster.job_title || "",
    salary_basis: runEmployee.salary_basis || employeeMaster.salary_basis || "",
    currency_code:
      runEmployee.currency_code || employeeMaster.currency_code || "NGN",

    // take bank details from employee master as requested
    bank_name: employeeMaster.bank_name || "",
    bank_account_name: employeeMaster.bank_account_name || "",
    bank_account_number: employeeMaster.bank_account_number || "",

    // level / grade from employee master
    job_level_name: jobLevel?.level_name || "",
    job_level_code: jobLevel?.level_code || "",
    job_grade_name: jobGrade?.grade_name || "",
    job_grade_code: jobGrade?.grade_code || "",
  };

  return {
    employeeSummary,
    breakdown: breakdownRes.data || [],
  };
}

export async function testPayrollRule(payload) {
  const { data, error } = await supabase.functions.invoke("test-payroll-rule", {
    body: payload,
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/* =========================================================
   COMPENSATION FRAMEWORK / LEVELS / GRADES
========================================================= */

export async function ensurePayrollPhase4Bootstrap(companyId) {
  if (!companyId) throw new Error("companyId is required");

  const { error } = await supabase.rpc("ensure_payroll_phase4_bootstrap", {
    p_company_id: companyId,
  });

  if (error) throw error;
}

export async function getPayrollJobLevels(companyId) {
  const { data, error } = await supabase
    .from("payroll_job_levels")
    .select("*")
    .eq("company_id", companyId)
    .order("level_rank", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createPayrollJobLevel(payload) {
  const { data, error } = await supabase
    .from("payroll_job_levels")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayrollJobLevel(id, payload) {
  const { data, error } = await supabase
    .from("payroll_job_levels")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollJobGrades(companyId, levelId = null) {
  let query = supabase
    .from("payroll_job_grades")
    .select(
      `
      *,
      payroll_job_levels (
        id,
        level_code,
        level_name,
        level_rank
      )
    `
    )
    .eq("company_id", companyId)
    .order("grade_rank", { ascending: true });

  if (levelId) {
    query = query.eq("level_id", levelId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

export async function createPayrollJobGrade(payload) {
  const { data, error } = await supabase
    .from("payroll_job_grades")
    .insert(payload)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updatePayrollJobGrade(id, payload) {
  const { data, error } = await supabase
    .from("payroll_job_grades")
    .update(payload)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollGradeFramework(companyId) {
  const { data, error } = await supabase
    .from("payroll_grade_framework_view")
    .select("*")
    .eq("company_id", companyId)
    .order("level_rank", { ascending: true })
    .order("grade_rank", { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function getPayrollGradeCompensationProfile(companyId, gradeId) {
  const { data, error } = await supabase
    .from("payroll_grade_compensation_profiles")
    .select("*")
    .eq("company_id", companyId)
    .eq("grade_id", gradeId)
    .maybeSingle();

  if (error) throw error;
  return data || null;
}

export async function upsertPayrollGradeCompensationProfile(payload) {
  const { data, error } = await supabase
    .from("payroll_grade_compensation_profiles")
    .upsert(payload, { onConflict: "grade_id" })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getPayrollGradeComponentDefaults(companyId, gradeId) {
  const { data, error } = await supabase
    .from("payroll_grade_component_defaults")
    .select(
      `
      *,
      payroll_pay_components (
        id,
        component_code,
        component_name,
        component_type,
        amount_type,
        taxable,
        pensionable
      )
    `
    )
    .eq("company_id", companyId)
    .eq("grade_id", gradeId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function upsertPayrollGradeComponentDefault(payload) {
  const { data, error } = await supabase
    .from("payroll_grade_component_defaults")
    .upsert(payload, { onConflict: "grade_id,component_id" })
    .select();

  if (error) throw error;
  return data;
}

export async function deletePayrollGradeComponentDefault(id) {
  const { error } = await supabase
    .from("payroll_grade_component_defaults")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function getPayrollGradeBenefitDefaults(companyId, gradeId) {
  const { data, error } = await supabase
    .from("payroll_grade_benefit_defaults")
    .select(
      `
      *,
      payroll_benefit_plans (
        id,
        benefit_code,
        benefit_name,
        benefit_type
      )
    `
    )
    .eq("company_id", companyId)
    .eq("grade_id", gradeId)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function upsertPayrollGradeBenefitDefault(payload) {
  const { data, error } = await supabase
    .from("payroll_grade_benefit_defaults")
    .upsert(payload, { onConflict: "grade_id,benefit_plan_id" })
    .select();

  if (error) throw error;
  return data;
}

export async function deletePayrollGradeBenefitDefault(id) {
  const { error } = await supabase
    .from("payroll_grade_benefit_defaults")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export async function applyGradeTemplateToEmployee({
  companyId,
  employeeId,
  gradeId,
  mode = "replace",
  appliedBy = null,
}) {
  const { data, error } = await supabase.functions.invoke(
    "apply-grade-template-to-employee",
    {
      body: {
        company_id: companyId,
        employee_id: employeeId,
        grade_id: gradeId,
        mode,
        applied_by: appliedBy,
      },
    }
  );

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

/* =========================================================
   REPORTS
========================================================= */

export async function getPayrollReports(companyId) {
  const [
    runsRes,
    employeesRes,
    payslipsRes,
    anomaliesRes,
    paymentsRes,
    loansRes,
    leaveReqRes,
  ] = await Promise.all([
    supabase
      .from("payroll_runs")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false }),

    supabase.from("payroll_employees").select("*").eq("company_id", companyId),

    supabase
      .from("payroll_payslips")
      .select("*")
      .eq("company_id", companyId)
      .order("created_at", { ascending: false }),

    supabase.from("payroll_anomalies").select("*").eq("company_id", companyId),

    supabase
      .from("payroll_payment_batches")
      .select("*")
      .eq("company_id", companyId),

    supabase.from("payroll_loans").select("*").eq("company_id", companyId),

    supabase
      .from("payroll_leave_requests")
      .select("*")
      .eq("company_id", companyId),
  ]);

  if (runsRes.error) throw runsRes.error;
  if (employeesRes.error) throw employeesRes.error;
  if (payslipsRes.error) throw payslipsRes.error;
  if (anomaliesRes.error) throw anomaliesRes.error;
  if (paymentsRes.error) throw paymentsRes.error;
  if (loansRes.error) throw loansRes.error;
  if (leaveReqRes.error) throw leaveReqRes.error;

  return {
    runs: runsRes.data || [],
    employees: employeesRes.data || [],
    payslips: payslipsRes.data || [],
    anomalies: anomaliesRes.data || [],
    payments: paymentsRes.data || [],
    loans: loansRes.data || [],
    leaveRequests: leaveReqRes.data || [],
  };
}
